import { useState } from "react";
import { describeError } from "@/api/client";
import { Button, Dialog, DialogClose, Form, InlineAlert, Select, TextArea, useToast } from "@/components";
import { useEnqueueJob, type JobKind } from "./api";

export interface EnqueueJobDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  kinds: readonly JobKind[];
}

/** Parses the payload text; an empty box means {}. */
export function parsePayload(text: string): { payload: Record<string, unknown> } | { error: string } {
  if (!text.trim()) return { payload: {} };
  try {
    const parsed: unknown = JSON.parse(text);
    if (parsed && typeof parsed === "object" && !Array.isArray(parsed))
      return { payload: parsed as Record<string, unknown> };
  } catch {
    // Fall through to the message below.
  }
  return { error: 'The payload must be a JSON object, for example {"supplier_id": "fraterworks"}.' };
}

/** Starts one of the job kinds the API allows (GET /api/jobs/kinds). */
export function EnqueueJobDialog({ open, onOpenChange, kinds }: EnqueueJobDialogProps) {
  const [kind, setKind] = useState<string | null>(null);
  const [payloadText, setPayloadText] = useState("");
  const [payloadError, setPayloadError] = useState<string | null>(null);
  const [kindError, setKindError] = useState<string | null>(null);
  const enqueue = useEnqueueJob();
  const toast = useToast();
  const formId = "enqueue-job-form";

  function close() {
    onOpenChange(false);
    setKind(null);
    setPayloadText("");
    setPayloadError(null);
    setKindError(null);
    enqueue.reset();
  }

  function handleSubmit() {
    const parsed = parsePayload(payloadText);
    setKindError(kind ? null : "Choose which job to start.");
    setPayloadError("error" in parsed ? parsed.error : null);
    if (!kind || "error" in parsed) return;
    enqueue.mutate(
      { kind, payload: parsed.payload },
      {
        onSuccess: () => {
          toast.success("Job started", { description: kinds.find((item) => item.kind === kind)?.description ?? kind });
          close();
        },
      },
    );
  }

  const selected = kinds.find((item) => item.kind === kind);

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => (next ? onOpenChange(true) : close())}
      title="Start a job"
      description="The job runs in the background worker; follow its progress on this page."
      footer={
        <>
          <DialogClose />
          <Button type="submit" form={formId} variant="primary" pending={enqueue.isPending} pendingLabel="Starting…">
            Start job
          </Button>
        </>
      }
    >
      <Form id={formId} onSubmit={handleSubmit} aria-label="Start a job">
        {enqueue.isError ? (
          <InlineAlert tone="danger" title="Couldn’t start the job">
            {describeError(enqueue.error)}
          </InlineAlert>
        ) : null}
        <Select
          label="Job"
          options={kinds.map((item) => ({ value: item.kind, label: item.description || item.kind, detail: item.kind }))}
          value={kind}
          onValueChange={(value) => {
            setKind(value);
            setKindError(null);
          }}
          placeholder="Choose a job…"
          error={kindError}
          description={selected ? `Runs ${selected.kind}.` : undefined}
        />
        <TextArea
          label="Payload"
          optional
          mono
          rows={5}
          value={payloadText}
          onValueChange={setPayloadText}
          placeholder='{"supplier_id": "fraterworks"}'
          description="Options for the job as a JSON object. Leave empty for the defaults."
          error={payloadError}
          spellCheck={false}
        />
      </Form>
    </Dialog>
  );
}
